/*
 * userTz — small wrapper around Intl.DateTimeFormat for rendering
 * timestamps in the user's local timezone.
 *
 * Splash, the AI chat assistant, gets ISO-UTC timestamps in tool_result
 * payloads (from the per-app DB) and was rendering them as raw "1:52 AM
 * (UTC)" — which is jarring for a US-Eastern user looking at "9:52 PM
 * yesterday" on the wall clock. The Layer 2 data_view rendering uses
 * `formatLocalTime()` for `kind:"datetime"` cells/axes; SplashAgent
 * also stamps `tz` + `locale` onto the per-turn ui_context so Splash
 * can speak in local time inside prose.
 *
 * SSR-safe: getUserTz / getUserLocale fall back to UTC + en-US when
 * Intl / navigator aren't defined. The SPA always runs in a browser so
 * this only matters if the file gets imported by tooling.
 */

const FALLBACK_TZ = 'UTC';
const FALLBACK_LOCALE = 'en-US';

export function getUserTz(): string {
  try {
    if (typeof Intl === 'undefined') return FALLBACK_TZ;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || FALLBACK_TZ;
  } catch {
    return FALLBACK_TZ;
  }
}

export function getUserLocale(): string {
  try {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    return FALLBACK_LOCALE;
  } catch {
    return FALLBACK_LOCALE;
  }
}

type FormatStyle = 'auto' | 'time' | 'date' | 'datetime';

interface FormatOpts {
  /** 'auto' picks based on age:
   *    same-day → 'time'   ("7:02 PM")
   *    different day, same year → 'datetime' ("Apr 28, 7:02 PM")
   *    different year → 'datetime' with year ("Apr 28, 2024, 7:02 PM")
   */
  style?: FormatStyle;
  /** Append the short timezone name ("EDT", "PST"). Default: false. */
  showTzName?: boolean;
}

/**
 * Format an ISO timestamp / Date / epoch-ms for display in the user's
 * local timezone. Returns the original input as a string if parsing
 * fails — better to show something than swallow the value entirely.
 */
export function formatLocalTime(
  value: string | number | Date | null | undefined,
  opts: FormatOpts = {},
): string {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const tz = getUserTz();
  const locale = getUserLocale();
  const style = opts.style ?? 'auto';

  // Compare local-tz calendar days, not UTC days. Constructing a tagged
  // YYYY-MM-DD string in the target tz is the simplest way.
  const dayInTz = (date: Date) =>
    new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: tz,
    }).format(date);

  const now = new Date();
  const sameDay = dayInTz(d) === dayInTz(now);
  const sameYear = d.getUTCFullYear() === now.getUTCFullYear();
  const tzNamePart = opts.showTzName ? { timeZoneName: 'short' as const } : {};

  if (style === 'time' || (style === 'auto' && sameDay)) {
    return new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz,
      ...tzNamePart,
    }).format(d);
  }

  if (style === 'date') {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
      timeZone: tz,
    }).format(d);
  }

  // 'datetime' or 'auto' (different day)
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
    ...(sameYear ? {} : { year: 'numeric' }),
    ...tzNamePart,
  }).format(d);
}

/**
 * Compact label for a chart x-axis tick. Drops the year and tz name,
 * keeps month/day + hour:minute. Matches Chart.js's default rotation
 * heuristic when there are many labels.
 */
export function formatChartTick(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const tz = getUserTz();
  const locale = getUserLocale();
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  }).format(d);
}
